import * as vp from 'vopay';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDataDog } from './Datadog.provider';
import useIQ11Login from '../hooks/useApi/usePostIQ11Login';
import usePostIQ11AuthPing from '../hooks/useApi/usePostIQ11AuthPing';
import useIQ11LoginDialogue from '../hooks/useDialogues/useIQ11LoginDialogue';
import useLoginStorage from '../hooks/useLoginStorage';
import { useToken } from './Token.provider';
import { useIQ11UserData } from './IQ11UserData.provider';
import { useCache } from './Cache.provider';
import type { IQ11LoginRequest } from '../hooks/useApi/usePostIQ11Login';

type IQ11UserLoginCtx = {
  isAuthenticated: boolean | null;
  isSubmitting: boolean;
  response: {
    clientAccount: vp.IQ11AuthorizationClientAccount | undefined;
    jwt: string;
    loginUserEmail: string;
    permissions: vp.IQ11Permissions | undefined;
  };
  login: (requestParams: IQ11LoginRequest) => void;
  logout: () => void;
};

export const IQ11UserLoginContext = createContext<IQ11UserLoginCtx>({
  isAuthenticated: false,
  isSubmitting: false,
  response: {
    clientAccount: {
      BusinessName: '',
      ClientAccountID: '',
      ClientDisplayName: '',
      ClientType: '',
      DateAdded: '',
      DateDeleted: '',
      EmailAddress: '',
      FirstName: '',
      LastName: '',
      IPAddress: '',
      IsActive: '',
      IsPrimaryAccount: '',
      LastModified: '',
      TransactionLimit: '',
      ID: 0,
      VIOClientAccountUsers_ID: 0,
      VIOAccounts_ID: 0,
      IsBillingAccount: '',
    },
    permissions: {
      BankAccountsEnabled: false,
      CreditCardsEnabled: false,
    },
    jwt: '',
    loginUserEmail: '',
  },

  login: () => {},
  logout: () => {},
});

export const IQ11UserLoginProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginResponse, refreshIQ11UserLogin] = useIQ11Login({});

  const [loginUserEmailRequest, setLoginUserEmail] = useState('');
  const { token } = useToken();
  const history = useHistory();
  const { showDialogue } = useIQ11LoginDialogue(loginResponse);
  const { clearCache } = useCache();
  const { setIQ11UserData, reset: clearIQ11UserData } = useIQ11UserData();
  const { setUser, clearUser } = useDataDog();
  const {
    login: { getIQ11UserLogin, storeIQ11UserLogin, clearIQ11UserLogin },
  } = useLoginStorage();

  const { clientAccount, jwt, loginUserEmail, clientAccountPermissions } = getIQ11UserLogin();
  const [tokenValidationResponse, validateTokens] = usePostIQ11AuthPing({ bearer: jwt });
  useEffect(() => {
    const handlers = {
      tokens: () => validateTokens(),
      noTokens: () => {
        setIsAuthenticated(false);
        // history.push('/');
      },
    };

    return hasStoredLoginData() ? handlers.tokens() : handlers.noTokens();
  }, []);

  useEffect(() => {
    const handlers = {
      success: () => {
        setIsAuthenticated(true);
        if (token) {
          history.push('/dashboard');
        } else {
          history.push('/dashboard');
        }
      },
      fail: () => {
        // needs to change
        if (
          tokenValidationResponse?.errorMessage !==
          'Authentication successful. Please specify a valid /account/client-accounts endpoint'
        ) {
          clearIQ11UserLogin();
          clearUser();
          setIsAuthenticated(false);
          if (token) {
            if (token?.HasLogin) {
              history.push('/');
            }
          } else {
            history.push('/');
          }
        } else {
          setIsAuthenticated(true);
        }
      },
    };
    if (tokenValidationResponse) return tokenValidationResponse.success ? handlers.success() : handlers.fail();

    return () => {};
  }, [tokenValidationResponse]);

  useEffect(() => {
    if (loginResponse && loginResponse?.success) {
      storeIQ11UserLogin(loginResponse, loginUserEmailRequest);
      setUser({
        username: loginUserEmailRequest,
        jwt: loginResponse.jwt,
        ClientAccountID: loginResponse.clientAccount.ClientAccountID,
      });
      setIsSubmitting(false);
    }
    if (loginResponse?.errorMessage) showDialogue();
  }, [loginResponse]);

  useEffect(() => {
    setIQ11UserData({
      clientAccount,
      jwt,
      loginUserEmail,
    });
  }, [clientAccount, jwt, loginUserEmail]);

  useEffect(() => {
    if (loginResponse?.success && hasStoredLoginData()) {
      setIsAuthenticated(true);
      if (token) {
        history.push('/dashboard');
      } else {
        history.push('/dashboard');
      }
    }
  }, [clientAccount, jwt]);

  const handleLogin = (requestParams: IQ11LoginRequest) => {
    setIsSubmitting(true);
    setLoginUserEmail(requestParams.EmailAddress);
    refreshIQ11UserLogin(requestParams);
  };

  const handleLogout = () => {
    clearCache();
    clearIQ11UserLogin();
    clearUser();
    clearIQ11UserData();
    setIsAuthenticated(false);
    history.push('/');
  };

  return (
    <IQ11UserLoginContext.Provider
      value={{
        isAuthenticated,
        isSubmitting,
        response: {
          clientAccount,
          jwt,
          loginUserEmail,
          permissions: clientAccountPermissions,
        },

        login: handleLogin,
        logout: handleLogout,
      }}
    >
      {children}
    </IQ11UserLoginContext.Provider>
  );

  function hasStoredLoginData() {
    return [clientAccount, jwt].every((loginToken) => Boolean(loginToken));
  }
};

export const useIQ11UserLogin = (): IQ11UserLoginCtx => useContext(IQ11UserLoginContext);
